import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ProfileWithName from '../components/ProfileWithName';
import Loading from '../components/Loading';
import Caret from '../components/Caret';

/**
 * 점령전 휴가(로테이션) 탭.
 *
 * 점령전은 **월요일·목요일**에 열린다. 달력에서 점령전 날은 테두리로 표시한다.
 *
 * **정하는 것은 휴가 하나다.** 길마·부길마가 왼쪽 길드원 판에서 오른쪽 휴가 판으로
 * 화살표로 옮긴다. 출전 인원은 그 나머지라서 화면에 따로 적지 않는다 — 왼쪽 판이 곧
 * 나가는 사람들이다.
 *
 * 길드원이 낸 신청은 오른쪽에 「신청」으로 **표시만** 된다 — 아직 휴가가 아니고, 길마가
 * 넣어야 휴가가 된다.
 *
 * 인원은 하한이다 — 한 번에 25명만 나가므로 명단에서 25명을 뺀 인원은 반드시 쉬어야 한다
 * (명단 28명이면 최소 3명). 더 쉬는 것도 막지 않는다. 잔소리는 하지 않고 휴가·참가 수만
 * 적어 둔다(넘치면 참가 숫자가 붉어진다).
 *
 * 데스크톱·모바일을 따로 만들지 않고 한 파일로 둔다 — 새로 만드는 화면이라 굳이 둘로
 * 갈라 놓을 이유가 없다. 크기만 `isMobile` 로 조절한다.
 */

interface VacationPerson {
    _id: string;
    uuid: string;
    nickname: string;
    profileImg: string | null;
    isDeleted: boolean;
    status: 'pending' | 'approved' | 'rejected';
    reason: string;
    mine: boolean;
}

/** 길드 명단의 한 사람 */
interface RosterPerson {
    wizardId: number;
    /** 사이트 계정이 있으면 그 uuid. 없으면 휴가로 체크할 수 없다 */
    uuid: string | null;
    nickname: string;
    profileImg: string | null;
}

interface VacationDay {
    date: string;
    past: boolean;
    /** 그 날 쉬는 사람 */
    approved: VacationPerson[];
    pending: VacationPerson[];
    /** 출전 인원 = 명단 - 휴가. 서버가 계산해 준다 */
    participants: RosterPerson[];
    /** 25명을 넘긴 인원. 이만큼 더 쉬어야 한다 */
    over: number;
    /** 최소 인원까지 남은 휴가 수 */
    short: number;
}

interface VacationData {
    year: number;
    month: number;
    today: string;
    roster: number;
    capacity: number;
    /** 한 번에 나갈 수 있는 인원(25) */
    maxParticipants: number;
    /** 반드시 쉬어야 하는 최소 인원 = 명단 - 25 */
    minResting: number;
    canManage: boolean;
    /** 신청 수락·거절 — 선임 이상 */
    canApprove: boolean;
    me: { uuid: string; nickname: string; role: string };
    /** 지금 게임 명단에 있는 사람들 */
    members: RosterPerson[];
    days: VacationDay[];
}

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 달력 칸에 이름을 몇 명까지 적을지. 넘치면 +N.
 * 예전엔 PC 3·폰 2 라 휴가 3~4명인 날도 이름이 잘렸다 — PC·폰 모두 5명(사용자 2026-10-22). 칸은 이름 수만큼 늘어난다.
 */
const SHOW_NAMES = 5;

const Vacation: React.FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
    const now = useMemo(() => new Date(), []);
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [data, setData] = useState<VacationData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openDate, setOpenDate] = useState<string | null>(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    /** 두 판에서 고른 사람들. 화살표로 옮길 대상이다 */
    const [selLeft, setSelLeft] = useState<string[]>([]);
    const [selRight, setSelRight] = useState<string[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`/api/vacations?year=${year}&month=${month}`, { credentials: 'include' });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setError(body.error || '휴가 정보를 불러오지 못했습니다.');
                setData(null);
                return;
            }
            setData(await res.json());
        } catch {
            setError('서버에 연결하지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => { load(); }, [load]);

    // 날짜를 바꾸면 골라 둔 것은 버린다
    useEffect(() => { setSelLeft([]); setSelRight([]); }, [openDate]);

    const act = async (run: () => Promise<Response>) => {
        setBusy(true);
        try {
            const res = await run();
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(body.error || '처리하지 못했습니다.');
                return false;
            }
            await load();
            return true;
        } catch {
            alert('서버에 연결하지 못했습니다.');
            return false;
        } finally {
            setBusy(false);
        }
    };

    const apply = (date: string, uuid?: string) => act(() => fetch('/api/vacations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ date, reason, uuid })
    })).then((ok) => { if (ok) setReason(''); });

    const cancel = (id: string) => act(() => fetch(`/api/vacations/${id}`, {
        method: 'DELETE',
        credentials: 'include'
    }));

    /**
     * 신청을 수락한다 (선임 이상 — 자기 신청도 된다).
     *
     * 화살표로 옮겨도 수락이 되지만, 신청은 **줄에서 바로 수락**하는 쪽이 자연스럽다 —
     * 신청이 온 줄에 단추가 있어야 무엇을 하라는 것인지 한눈에 보인다.
     */
    const decide = (id: string, status: 'approved' | 'rejected') => act(() => fetch(`/api/vacations/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status })
    }));
    const approve = (id: string) => decide(id, 'approved');

    /** 그 날 쉬는 사람을 한 번에 저장한다. 되돌려 보낸 신청은 거절로 남는다 */
    const saveResting = (date: string, uuids: string[], reject: string[]) => act(() => fetch(`/api/vacations/${date}/resting`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ uuids, reject })
    }));

    const move = (delta: number) => {
        const m = month + delta;
        if (m < 1) { setYear(year - 1); setMonth(12); }
        else if (m > 12) { setYear(year + 1); setMonth(1); }
        else setMonth(m);
        setOpenDate(null);
    };

    /** 달력 칸: 1일이 시작하는 요일만큼 앞을 비운다 */
    const cells = useMemo(() => {
        const first = new Date(Date.UTC(year, month - 1, 1, 12)).getUTCDay();
        const lastDay = new Date(Date.UTC(year, month, 0, 12)).getUTCDate();
        const out: Array<{ date: string; day: number } | null> = Array(first).fill(null);
        for (let d = 1; d <= lastDay; d += 1) {
            out.push({ date: `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`, day: d });
        }
        return out;
    }, [year, month]);

    const dayOf = (date: string) => data?.days.find((d) => d.date === date) || null;
    const opened = openDate ? dayOf(openDate) : null;

    const pendingByUuid = useMemo(
        () => new Map((opened?.pending || []).map((p) => [p.uuid, p])),
        [opened]
    );
    const memberByUuid = useMemo(
        () => new Map((data?.members || []).filter((m) => m.uuid).map((m) => [m.uuid as string, m])),
        [data?.members]
    );

    /**
     * 오른쪽 판: 쉬는 사람 + 아직 신청만 한 사람.
     *
     * 신청은 **표시만** 한다 — 오른쪽에 보이지만 휴가는 아니다. 길마가 화살표로 넣어야
     * 휴가가 되고, 되돌려 보내면 거절된다.
     */
    const rightRows = useMemo(() => {
        const shape = (p: VacationPerson, pending: boolean) => ({
            uuid: p.uuid,
            id: p._id,
            person: memberByUuid.get(p.uuid)
                || { wizardId: -1, uuid: p.uuid, nickname: p.nickname, profileImg: p.profileImg },
            pending,
            mine: p.mine,
            reason: p.reason
        });
        return [
            ...(opened?.approved || []).map((p) => shape(p, false)),
            ...(opened?.pending || []).map((p) => shape(p, true))
        ];
    }, [opened, memberByUuid]);

    /** 왼쪽 판: 오른쪽에 없는 길드원 */
    const leftRows = useMemo(() => {
        const onRight = new Set(rightRows.map((r) => r.uuid));
        return (data?.members || []).filter((m) => !m.uuid || !onRight.has(m.uuid));
    }, [data?.members, rightRows]);

    const toggle = (list: string[], uuid: string) =>
        list.includes(uuid) ? list.filter((u) => u !== uuid) : [...list, uuid];

    /** 지금 쉬는 사람들 */
    const approvedUuids = useMemo(() => (opened?.approved || []).map((p) => p.uuid), [opened]);

    /** 오른쪽 판에 있는 내 줄. 신청했거나 이미 휴가다 */
    const myRow = useMemo(() => rightRows.find((r) => r.mine), [rightRows]);

    /** 내 신청·휴가를 뺀다 */
    const cancelMine = async () => {
        if (!opened) return;
        const doc = [...opened.pending, ...opened.approved].find((p) => p.mine);
        if (doc) await cancel(doc._id);
    };

    /**
     * 고른 사람을 휴가로.
     *
     * 길마는 한 번에 넣고(신청도 이때 승인된다), 길드원은 자기 신청을 낸다.
     */
    const moveToVacation = async () => {
        if (!opened || !selLeft.length) return;
        if (data?.canManage) {
            const add = selLeft.filter((u) => !approvedUuids.includes(u));
            await saveResting(opened.date, approvedUuids.concat(add), []);
        } else {
            await apply(opened.date);
        }
        setSelLeft([]);
        setSelRight([]);
    };

    /**
     * 고른 사람을 길드원 쪽으로.
     *
     * 길마가 신청을 되돌려 보내는 것은 거절이다. 길드원은 자기 신청·휴가만 취소한다.
     */
    const moveToRoster = async () => {
        if (!opened || !selRight.length) return;
        if (data?.canManage) {
            await saveResting(
                opened.date,
                approvedUuids.filter((u) => !selRight.includes(u)),
                selRight.filter((u) => pendingByUuid.has(u))
            );
        } else {
            const mine = rightRows.find((r) => r.mine && selRight.includes(r.uuid));
            const doc = mine && [...opened.pending, ...opened.approved].find((p) => p.uuid === mine.uuid);
            if (doc) await cancel(doc._id);
        }
        setSelRight([]);
    };

    const size = {
        cell: isMobile ? 'clamp(52px, 13vw, 68px)' : 96,
        num: isMobile ? 'clamp(10px, 2.2vw, 12px)' : 13,
        name: isMobile ? 'clamp(8px, 1.8vw, 10px)' : 11,
        pad: isMobile ? 'clamp(10px, 2.5vw, 14px)' : 20
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            padding: isMobile ? 'clamp(12px, 3vw, 20px) clamp(6px, 2vw, 12px)' : '32px 20px'
        }}>
            <div style={{ maxWidth: 1000, margin: '0 auto' }}>
                {/* 머리말 */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 20,
                    padding: size.pad,
                    marginBottom: 16,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}>
                    <h2 style={{
                        margin: 0,
                        fontSize: isMobile ? 'clamp(18px, 4vw, 22px)' : 26,
                        fontWeight: 800,
                        color: '#667eea',
                        textAlign: 'center'
                    }}>휴가 (로테이션)</h2>
                    <p style={{
                        margin: '6px 0 0',
                        fontSize: isMobile ? 'clamp(11px, 2.4vw, 12px)' : 14,
                        color: '#666',
                        textAlign: 'center',
                        fontWeight: 500
                    }}>
                        점령전 날을 눌러 휴가 인원을 설정해주세요
                    </p>
                </div>

                {/* 달 이동 */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 16,
                    marginBottom: 12
                }}>
                    <button onClick={() => move(-1)} style={navBtn}><Caret style={{ transform: 'rotate(180deg)' }} /></button>
                    <div style={{
                        color: '#fff',
                        fontWeight: 800,
                        fontSize: isMobile ? 'clamp(14px, 3.5vw, 17px)' : 20,
                        minWidth: 130,
                        textAlign: 'center'
                    }}>{year}년 {month}월</div>
                    <button onClick={() => move(1)} style={navBtn}><Caret /></button>
                </div>

                {error && (
                    <div style={{
                        background: 'rgba(255, 107, 107, 0.15)',
                        border: '1px solid rgba(255, 107, 107, 0.4)',
                        color: '#fff',
                        borderRadius: 12,
                        padding: '12px 16px',
                        marginBottom: 12,
                        fontWeight: 600,
                        textAlign: 'center'
                    }}>{error}</div>
                )}

                {/* 달력 */}
                <div style={{
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: 20,
                    padding: size.pad,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
                }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? 3 : 6 }}>
                        {WEEKDAY_LABELS.map((w, i) => (
                            <div key={w} style={{
                                textAlign: 'center',
                                fontSize: size.num,
                                fontWeight: 800,
                                color: i === 0 ? '#e57373' : i === 6 ? '#64B5F6' : '#666',
                                paddingBottom: 6
                            }}>{w}</div>
                        ))}

                        {cells.map((c, i) => {
                            if (!c) return <div key={`e${i}`} />;
                            const day = dayOf(c.date);
                            const isSiege = !!day;
                            const isToday = data?.today === c.date;
                            return (
                                <div
                                    key={c.date}
                                    onClick={() => isSiege && setOpenDate(openDate === c.date ? null : c.date)}
                                    style={{
                                        minHeight: size.cell,
                                        borderRadius: 12,
                                        padding: isMobile ? 4 : 8,
                                        cursor: isSiege ? 'pointer' : 'default',
                                        // 점령전 날만 도드라지게. 지난 날은 눌러 둔다
                                        background: isSiege
                                            ? (openDate === c.date
                                                ? 'rgba(102, 126, 234, 0.24)'
                                                : day!.past ? 'rgba(102, 126, 234, 0.06)' : 'rgba(102, 126, 234, 0.12)')
                                            : 'rgba(0, 0, 0, 0.02)',
                                        // 점령전 날은 테두리를 칠하고, 고른 날은 진하게 눌린 느낌으로
                                        border: isSiege
                                            ? `${openDate === c.date ? 3 : 2}px solid ${openDate === c.date ? '#4f46e5' : day!.past ? 'rgba(102, 126, 234, 0.45)' : '#667eea'}`
                                            : '1px solid rgba(102, 126, 234, 0.10)',
                                        // 고른 날은 테두리 밖으로 한 겹 더 두르고, 오늘은 주황으로
                                        boxShadow: openDate === c.date
                                            ? '0 0 0 3px rgba(79, 70, 229, 0.30), inset 0 2px 6px rgba(79, 70, 229, 0.18)'
                                            : isToday ? '0 0 0 2px #ffb74d' : 'none',
                                        transform: openDate === c.date ? 'translateY(1px)' : 'none',
                                        opacity: isSiege && day!.past ? 0.65 : 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2,
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <span style={{ fontSize: size.num, fontWeight: 700, color: '#333' }}>{c.day}</span>
                                        {isSiege && day!.approved.length > 0 && (
                                            <span style={{
                                                fontSize: isMobile ? 8 : 10,
                                                fontWeight: 800,
                                                color: '#4f46e5'
                                            }}>휴가</span>
                                        )}
                                    </div>
                                    {isSiege && day!.approved.slice(0, SHOW_NAMES).map((p) => (
                                        <div key={p._id} style={{
                                            fontSize: size.name,
                                            fontWeight: 600,
                                            color: p.isDeleted ? '#ff6b6b' : '#4f46e5',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>{p.nickname}</div>
                                    ))}
                                    {isSiege && day!.approved.length > SHOW_NAMES && (
                                        <div style={{ fontSize: size.name, color: '#888' }}>+{day!.approved.length - SHOW_NAMES}</div>
                                    )}
                                    {isSiege && day!.pending.length > 0 && (
                                        <div style={{ fontSize: size.name, color: '#ff9800', fontWeight: 700 }}>대기 {day!.pending.length}</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    {loading && <Loading padding={16} size={22} />}
                </div>

                {/* 고른 날 상세 */}
                {opened && data && (
                    <div style={{
                        background: 'rgba(255, 255, 255, 0.97)',
                        borderRadius: 20,
                        padding: size.pad,
                        marginTop: 16,
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: isMobile ? 15 : 18, color: '#333' }}>
                                {opened.date} ({WEEKDAY_LABELS[new Date(`${opened.date}T12:00:00Z`).getUTCDay()]})
                            </strong>
                            {opened.past && <span style={{ fontSize: 12, color: '#999' }}>지난 점령전</span>}
                        </div>

                        {/*
                          * 왼쪽 길드원 · 오른쪽 휴가. 늘 이 표만 있고, 여기서 바로 옮긴다.
                          * 길마는 누구든, 길드원은 자기 자신만 옮길 수 있다(=신청).
                          */}
                        <div style={{
                            display: 'grid',
                            // 모바일에서도 좌우로 둔다 - 위아래로 쌓으면 옮기는 방향이 헷갈린다
                            gridTemplateColumns: '1fr auto 1fr',
                            gap: isMobile ? 4 : 12,
                            alignItems: 'stretch'
                        }}>
                            <Pane title={`길드원 (${leftRows.length}명)`}>
                                {leftRows.map((m) => {
                                    const movable = !!m.uuid && !opened.past && (data.canManage || m.uuid === data.me.uuid);
                                    return (
                                        <PickRow
                                            key={m.wizardId}
                                            nickname={m.nickname}
                                            profileImg={m.profileImg}
                                            selected={!!m.uuid && selLeft.includes(m.uuid)}
                                            disabled={!m.uuid}
                                            readOnly={!movable}
                                            note={!m.uuid ? '계정 없음' : m.uuid === data.me.uuid ? '나' : undefined}
                                            tooltip={!m.uuid ? '사이트 계정이 없어 휴가로 넣을 수 없습니다' : undefined}
                                            isMobile={isMobile}
                                            onClick={() => m.uuid && setSelLeft(toggle(selLeft, m.uuid))}
                                        />
                                    );
                                })}
                            </Pane>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 8,
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}>
                                <button
                                    title={data.canManage ? '휴가로 넣기' : '휴가 신청'}
                                    disabled={busy || opened.past || selLeft.length === 0}
                                    onClick={moveToVacation}
                                    style={arrowBtn}
                                ><Caret /></button>
                                <button
                                    title={data.canManage ? '휴가에서 빼기' : '신청 취소'}
                                    disabled={busy || opened.past || selRight.length === 0}
                                    onClick={moveToRoster}
                                    style={arrowBtn}
                                ><Caret style={{ transform: 'rotate(180deg)' }} /></button>
                            </div>

                            <Pane title={`휴가 (${opened.approved.length}명)`}>
                                {rightRows.map((r) => {
                                    const movable = !opened.past && (data.canManage || r.mine);
                                    return (
                                        <PickRow
                                            key={r.uuid}
                                            nickname={r.person.nickname}
                                            profileImg={r.person.profileImg}
                                            selected={selRight.includes(r.uuid)}
                                            pending={r.pending}
                                            readOnly={!movable}
                                            reason={r.reason || undefined}
                                            isMobile={isMobile}
                                            /*
                                              신청은 줄에서 바로 수락한다 (마스터·부마스터).
                                              무르는 것은 화살표(◀)와 [내 휴가] 칸에 있으므로 줄에는
                                              두지 않는다 — 지우는 단추가 줄마다 있으면 잘못 누르기 쉽다.
                                            */
                                            action={r.pending && data.canApprove && !opened.past ? (
                                                <>
                                                    <button
                                                        disabled={busy}
                                                        onClick={() => decide(r.id, 'rejected')}
                                                        style={rejectBtn}
                                                    >반려</button>
                                                    <button
                                                        disabled={busy}
                                                        onClick={() => approve(r.id)}
                                                        style={acceptBtn}
                                                    >승인</button>
                                                </>
                                            ) : undefined}
                                            onClick={() => setSelRight(toggle(selRight, r.uuid))}
                                        />
                                    );
                                })}
                            </Pane>
                        </div>
                        <div style={{ height: 16 }} />

                        {/*
                          * 내 휴가. 표에서 화살표로도 되지만, 버튼이 없으면 어디서 신청하는지
                          * 찾을 수 없다 — 자기 것은 여기서 한 번에 한다.
                          */}
                        {!opened.past && (
                            <Section title="내 휴가">
                                {myRow ? (
                                    /**
                                     * 한 줄에 [상태] [사유] [취소] 를 나란히 둔다.
                                     *
                                     * 예전에는 "신청했습니다 (아직 휴가는 아닙니다)" 를 굵은 글씨로 길게
                                     * 적고 그 옆에 사유를 맨몸으로 붙여 놔서, 회색 덩어리처럼 보이고
                                     * 어느 글자가 사유인지 알 수 없었다. 상태는 색 있는 알약으로,
                                     * 사유는 `사유` 라벨을 단 칸으로 감싸 각각이 무엇인지 드러낸다.
                                     * 세 조각의 높이도 맞춘다 — 제각각인 것이 지저분해 보이던 원인이다.
                                     */
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                                        <span
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 6,
                                                padding: '7px 12px',
                                                borderRadius: 999,
                                                fontSize: 12,
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                                color: myRow.pending ? '#b45309' : '#4338ca',
                                                background: myRow.pending ? 'rgba(255, 152, 0, 0.12)' : 'rgba(102, 126, 234, 0.12)'
                                            }}
                                        >
                                            <span
                                                style={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    background: myRow.pending ? '#ff9800' : '#4f46e5'
                                                }}
                                            />
                                            {myRow.pending ? '신청 대기' : '휴가'}
                                        </span>
                                        {myRow.reason && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 6,
                                                    minWidth: 0,
                                                    padding: '6px 12px',
                                                    borderRadius: 999,
                                                    border: '1px solid rgba(102, 126, 234, 0.2)',
                                                    background: '#fff',
                                                    fontSize: 12
                                                }}
                                            >
                                                <span style={{ color: '#aaa', fontWeight: 700, flexShrink: 0 }}>사유</span>
                                                <span
                                                    title={myRow.reason}
                                                    style={{
                                                        color: '#555',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {myRow.reason}
                                                </span>
                                            </span>
                                        )}
                                        {/*
                                          내 신청도 내가 수락한다 — 마스터·부마스터면.
                                          자기 것이라고 막으면 길마가 자기 휴가를 넣으려고 화살표를
                                          찾아 표로 올라가야 했다.
                                        */}
                                        {myRow.pending && data.canApprove && (
                                            <button
                                                disabled={busy}
                                                onClick={() => approve(myRow.id)}
                                                style={{ ...acceptBtn, padding: '7px 14px', marginLeft: 'auto', flexShrink: 0 }}
                                            >승인</button>
                                        )}
                                        {/* 알약과 같은 둥근 모양으로. `ghostBtn` 은 표에서도 쓰므로
                                            여기서만 덮어쓴다 */}
                                        <button
                                            disabled={busy}
                                            onClick={cancelMine}
                                            style={{
                                                ...ghostBtn,
                                                borderRadius: 999,
                                                // 승인 단추가 있으면 그쪽이 오른쪽으로 밀어 준다
                                                marginLeft: myRow.pending && data.canApprove ? 0 : 'auto',
                                                flexShrink: 0
                                            }}
                                        >
                                            {myRow.pending ? '신청 취소' : '휴가 빼기'}
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <input
                                            value={reason}
                                            onChange={(e) => setReason(e.target.value)}
                                            placeholder="사유 (선택)"
                                            maxLength={200}
                                            style={{
                                                flex: '1 1 180px',
                                                minWidth: 140,
                                                padding: '10px 12px',
                                                borderRadius: 10,
                                                border: '2px solid rgba(102, 126, 234, 0.2)',
                                                fontSize: 14,
                                                outline: 'none'
                                            }}
                                        />
                                        <button disabled={busy} onClick={() => apply(opened.date)} style={primaryBtn}>
                                            휴가 신청
                                        </button>
                                    </div>
                                )}
                            </Section>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const navBtn: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.9)',
    border: 'none',
    borderRadius: 10,
    width: 36,
    height: 36,
    color: '#667eea',
    fontWeight: 800,
    cursor: 'pointer'
};

const primaryBtn: React.CSSProperties = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    padding: '9px 16px',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer'
};

const ghostBtn: React.CSSProperties = {
    background: 'rgba(102, 126, 234, 0.1)',
    color: '#667eea',
    border: '1px solid rgba(102, 126, 234, 0.25)',
    borderRadius: 10,
    padding: '7px 12px',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer'
};

const arrowBtn: React.CSSProperties = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    width: 40,
    height: 34,
    fontSize: 14,
    fontWeight: 800,
    cursor: 'pointer'
};

/** 신청 줄의 [승인] — 눌러야 휴가가 된다 */
const acceptBtn: React.CSSProperties = {
    background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: 999,
    padding: '3px 10px',
    fontSize: 11,
    fontWeight: 800,
    cursor: 'pointer',
    whiteSpace: 'nowrap'
};

/** 신청 줄의 [반려] — 승인 옆, 덜 눈에 띄게 */
const rejectBtn: React.CSSProperties = {
    ...acceptBtn,
    background: 'transparent',
    color: '#e57373',
    border: '1px solid rgba(229, 115, 115, 0.6)'
};

/** 아직 휴가가 아니라는 표시 */
const pendingTag: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 800,
    color: '#ff9800',
    border: '1px solid rgba(255, 152, 0, 0.5)',
    borderRadius: 999,
    padding: '1px 6px',
    whiteSpace: 'nowrap'
};

/** 두 판 중 한 쪽 */
const Pane: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{
        border: '1px solid rgba(102, 126, 234, 0.18)',
        borderRadius: 12,
        padding: 8,
        background: 'rgba(248, 250, 255, 0.7)',
        maxHeight: 320,
        overflowY: 'auto'
    }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#667eea', padding: '2px 4px 6px' }}>{title}</div>
        {children}
    </div>
);

/** 판 안의 한 사람. 눌러서 고르고, 화살표로 옮긴다 */
const PickRow: React.FC<{
    nickname: string;
    profileImg: string | null;
    selected: boolean;
    isMobile: boolean;
    pending?: boolean;
    /** 될 수 없는 사람 — 흐리게 하고 이유를 말풍선으로 */
    disabled?: boolean;
    /** 옮길 권한이 없어 구경만 하는 줄. 흐리게 하지 않는다 */
    readOnly?: boolean;
    /** 이름 옆 작은 글씨 ('나'·'계정 없음' 처럼 짧은 것) */
    note?: string;
    /**
     * 휴가 사유. 길어질 수 있어 **폰에서는 이름 아래 줄로 내려** 접어 보여준다.
     *
     * 이름 옆에 두면 좁은 화면에서 칸이 쪼그라들어 잘리거나(…) 글자가 한 자씩 세로로 선다.
     * 데스크톱은 자리가 넉넉해 예전처럼 이름 옆 한 줄로 둔다.
     */
    reason?: string;
    tooltip?: string;
    /** 줄 아래에 붙는 단추 (신청 반려·승인). 폰에서는 이름 옆에 두면 칸 밖으로 밀려나 아래 줄에 둔다 */
    action?: React.ReactNode;
    onClick: () => void;
}> = ({ nickname, profileImg, selected, isMobile, pending, disabled, readOnly, note, reason, tooltip, action, onClick }) => {
    /** 폰에서 사유가 있으면 두 줄짜리 줄이 된다 — 위는 이름, 아래는 사유 */
    const stacked = isMobile && !!reason;

    const head = (
        <>
            <ProfileWithName profileImg={profileImg || undefined} nickname={nickname}
                size={isMobile ? 20 : 24} fontSize={isMobile ? 11 : 13} borderRadius={6} color="#333" />
            {pending && <span style={pendingTag}>신청</span>}
            {note && <span style={{ fontSize: 10, color: '#999', flexShrink: 0 }}>{note}</span>}
            {/*
              데스크톱은 자리가 넉넉해 사유를 이름 옆에 한 줄로 둔다. 넘치면 … 로 자르고
              전체는 말풍선으로 본다.
            */}
            {!stacked && reason && (
                <span
                    title={reason}
                    style={{
                        fontSize: isMobile ? 10 : 11,
                        color: '#999',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {reason}
                </span>
            )}
        </>
    );

    return (
        <div
            onClick={disabled || readOnly ? undefined : onClick}
            title={tooltip}
            style={{
                display: 'flex',
                flexDirection: stacked || action ? 'column' : 'row',
                alignItems: stacked || action ? 'stretch' : 'center',
                gap: stacked || action ? 3 : 8,
                padding: '6px 8px',
                marginBottom: 4,
                borderRadius: 8,
                background: selected ? 'rgba(102, 126, 234, 0.18)' : 'rgba(255, 255, 255, 0.9)',
                border: `1px ${pending ? 'dashed rgba(255, 152, 0, 0.6)' : `solid ${selected ? 'rgba(102, 126, 234, 0.5)' : 'rgba(102, 126, 234, 0.12)'}`}`,
                cursor: disabled ? 'not-allowed' : readOnly ? 'default' : 'pointer',
                opacity: disabled ? 0.5 : 1,
                userSelect: 'none'
            }}
        >
            {stacked ? (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{head}</div>
                    {/* 사유는 자르지 않고 접는다 — 폰에서는 가로가 모자라 자르면 못 읽는다 */}
                    <div
                        style={{
                            fontSize: 10,
                            color: '#999',
                            lineHeight: 1.45,
                            paddingLeft: 2,
                            overflowWrap: 'anywhere'
                        }}
                    >
                        {reason}
                    </div>
                </>
            ) : action ? <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>{head}</div> : head}
            {/* 단추는 줄을 고르는 것과 다른 일이므로 클릭이 위로 새지 않게 막는다 */}
            {action && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 2 }} onClick={(e) => e.stopPropagation()}>{action}</div>
            )}
        </div>
    );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#667eea', marginBottom: 8, letterSpacing: 0.3 }}>{title}</div>
        {children}
    </div>
);

export default Vacation;
